import { useMemo } from 'react';
import type { SessionStats, ClefSelection } from '../types/music';
import { getDifficultyDisplayName } from '../utils/adaptiveLogic';

interface StatsBarProps {
  stats: SessionStats;
  clefSelection: ClefSelection;
  progressionStages: { treble: number; bass: number };
  className?: string;
}

/**
 * Stats bar component displaying session statistics
 *
 * Shows:
 * - Total cards answered
 * - Accuracy percentage
 * - Average response time
 * - Current difficulty level
 */
export function StatsBar({
  stats,
  clefSelection,
  progressionStages,
  className = ''
}: StatsBarProps) {
  const accuracy = useMemo(() => {
    if (stats.totalCards === 0) return 0;
    return Math.round((stats.correctAnswers / stats.totalCards) * 100);
  }, [stats.totalCards, stats.correctAnswers]);

  const avgTime = useMemo(() => {
    if (stats.totalCards === 0) return 0;
    return (stats.averageResponseTime / 1000).toFixed(1);
  }, [stats.averageResponseTime, stats.totalCards]);

  const currentLevel = useMemo(() => {
    // Determine level based on progression stage
    const stage = clefSelection === 'treble'
      ? progressionStages.treble
      : clefSelection === 'bass'
        ? progressionStages.bass
        : Math.max(progressionStages.treble, progressionStages.bass);

    if (stage <= 1) return 'beginner';
    if (stage <= 2) return 'intermediate';
    return 'advanced';
  }, [clefSelection, progressionStages]);

  const clefDisplay = useMemo(() => {
    switch (clefSelection) {
      case 'treble': return 'Treble';
      case 'bass': return 'Bass';
      case 'mixed': return 'Mixed';
    }
  }, [clefSelection]);

  return (
    <div className={`stats-bar ${className}`}>
      <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8">
        {/* Cards Answered */}
        <StatItem
          label="Cards"
          value={stats.totalCards.toString()}
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          }
        />

        {/* Accuracy */}
        <StatItem
          label="Accuracy"
          value={`${accuracy}%`}
          valueColor={accuracy >= 80 ? 'text-green-600' : accuracy >= 60 ? 'text-yellow-600' : 'text-red-600'}
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />

        {/* Average Time */}
        <StatItem
          label="Avg Time"
          value={`${avgTime}s`}
          valueColor={Number(avgTime) <= 2 ? 'text-green-600' : Number(avgTime) <= 4 ? 'text-yellow-600' : 'text-red-600'}
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />

        {/* Clef */}
        <StatItem
          label="Clef"
          value={clefDisplay}
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
            </svg>
          }
        />

        {/* Level */}
        <StatItem
          label="Level"
          value={getDifficultyDisplayName(currentLevel === 'beginner' ? 'easy' : currentLevel === 'intermediate' ? 'medium' : 'hard')}
          valueColor={
            currentLevel === 'advanced' ? 'text-purple-600' :
            currentLevel === 'intermediate' ? 'text-blue-600' : 'text-gray-600'
          }
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          }
        />
      </div>
    </div>
  );
}

interface StatItemProps {
  label: string;
  value: string;
  icon?: React.ReactNode;
  valueColor?: string;
}

function StatItem({ label, value, icon, valueColor = 'text-gray-800' }: StatItemProps) {
  return (
    <div className="flex items-center gap-2">
      {icon && <span className="text-gray-400">{icon}</span>}
      <div className="text-center">
        <p className={`font-semibold ${valueColor}`}>{value}</p>
        <p className="text-xs text-gray-500">{label}</p>
      </div>
    </div>
  );
}

/**
 * Compact stats display for mobile
 */
export function StatsBarCompact({
  stats,
  className = ''
}: {
  stats: SessionStats;
  className?: string;
}) {
  const accuracy = stats.totalCards > 0
    ? Math.round((stats.correctAnswers / stats.totalCards) * 100)
    : 0;

  return (
    <div className={`flex items-center gap-4 text-sm ${className}`}>
      <span className="text-gray-600">
        {stats.totalCards} cards
      </span>
      <span className={`font-medium ${accuracy >= 80 ? 'text-green-600' : 'text-gray-600'}`}>
        {accuracy}% correct
      </span>
    </div>
  );
}
